import React from 'react';
import AiStorefrontCreateDialog from './AiStorefrontCreateDialog';
import AiGenerationProgress from './AiGenerationProgress';

interface IAiStorefrontCreateFlowProps {
    isOpen: boolean;
    /**
     * Resume an existing generation instead of starting at the form. The Getting Started checklist
     * deep-links back into a running session this way.
     */
    sessionId?: string;
    /** Close the flow. A running generation carries on server-side. */
    onDismiss: () => void;
    /** The user is starting over; the caller reopens the template picker. */
    onStartOver: () => void;
    /** The generated storefront was published. */
    onCompleted: (storefrontId?: string) => void;
}

/**
 * The guided create flow, as a two-step machine: the form, then the progress and review view.
 *
 * The step is just "do we have a session yet". Nothing is unmounted between them by accident: the
 * form is replaced by the progress view the moment the API hands back a session id, and reopening
 * the flow with a `sessionId` prop lands straight on the progress view.
 */
const AiStorefrontCreateFlow: React.FC<IAiStorefrontCreateFlowProps> = (props) => {
    const [sessionId, setSessionId] = React.useState<string | undefined>(props.sessionId);

    // Only reset on open/close and on a new resume target. A session started from the form inside
    // this flow does not change these, so it survives.
    React.useEffect(() => {
        setSessionId(props.isOpen ? props.sessionId : undefined);
    }, [props.isOpen, props.sessionId]);

    if (!props.isOpen) return null;

    if (!sessionId) {
        return (
            <AiStorefrontCreateDialog
                onDismiss={props.onDismiss}
                onStarted={(id) => setSessionId(id)}
            />
        );
    }

    return (
        <AiGenerationProgress
            key={sessionId}
            sessionId={sessionId}
            onDismiss={props.onDismiss}
            // Try Again replaced the session server-side. Pointing at the new id remounts the
            // progress dialog (it is keyed on the session), which is exactly the reset we want.
            onSwitchSession={(id) => setSessionId(id)}
            onStartOver={() => {
                setSessionId(undefined);
                props.onStartOver();
            }}
            onCompleted={(storefrontId) => {
                setSessionId(undefined);
                props.onCompleted(storefrontId);
            }}
        />
    );
};

export default AiStorefrontCreateFlow;
